import type { MaybeRef } from 'vue';

import { useIntersectionObserver } from '@vueuse/core';
import {
	computed,
	onUnmounted,
	readonly,
	ref,
	shallowReactive,
	unref
} from 'vue';

export type FloatingCtaTarget = 'shorts' | 'similar' | 'chosenForYou';

export interface SectionTarget {
	key: FloatingCtaTarget;
	priority: number;
}

const SECTION_PRIORITY: Record<FloatingCtaTarget, number> = {
	shorts: 3,
	similar: 2,
	chosenForYou: 1
};

interface SectionEntry {
	/** Ref to the element (either owned or from source) */
	elementRef: { value: HTMLElement | null };
	isVisible: ReturnType<typeof ref<boolean>>;
	hasScrolledPast: ReturnType<typeof ref<boolean>>;
	stop: () => void;
}

interface UseFloatingCtaOptions {
	heroSectionRef?: MaybeRef<HTMLElement | null>;
}

export function useFloatingCta(options: UseFloatingCtaOptions = {}) {
	const sections = shallowReactive(new Map<FloatingCtaTarget, SectionEntry>());

	const isHeroVisible = ref(true);

	const heroElement = computed(() => unref(options.heroSectionRef) ?? null);

	const { stop: stopHeroObserver } = useIntersectionObserver(
		heroElement,
		entries => {
			const entry = entries[0];
			if (!entry) return;
			isHeroVisible.value = entry.isIntersecting;
		},
		{ threshold: 0 }
	);

	function registerSection(
		key: FloatingCtaTarget,
		options?: { source?: MaybeRef<HTMLElement | null> }
	): ReturnType<typeof ref<HTMLElement | null>> {
		const elRef = ref<HTMLElement | null>(null);
		const observedRef = options?.source
			? computed(() => unref(options.source) ?? null)
			: elRef;
		const isVisible = ref(false);
		const hasScrolledPast = ref(false);

		const { stop } = useIntersectionObserver(
			observedRef,
			entries => {
				const entry = entries[0];
				if (!entry) return;

				isVisible.value = entry.isIntersecting;
				hasScrolledPast.value = entry.isIntersecting
					? false
					: entry.boundingClientRect.bottom <= 0;
			},
			{ threshold: 0 }
		);

		const elementRef = computed(() => unref(observedRef));
		sections.set(key, { elementRef, isVisible, hasScrolledPast, stop });
		return elRef;
	}

	onUnmounted(() => {
		stopHeroObserver();
		sections.forEach(entry => entry.stop());
	});

	// Priority: Shorts > Similar > Chosen For You (higher number = higher priority)
	const floatingCtaTarget = computed<FloatingCtaTarget | null>(() => {
		const entries = Array.from(sections.entries())
			.filter(([, entry]) => entry.elementRef.value)
			.sort(([a], [b]) => SECTION_PRIORITY[b] - SECTION_PRIORITY[a]);

		return entries[0]?.[0] ?? null;
	});

	const targetRef = computed<HTMLElement | null>(() => {
		const target = floatingCtaTarget.value;
		return target ? (sections.get(target)?.elementRef.value ?? null) : null;
	});

	const isTargetSectionVisible = computed<boolean>(() => {
		const target = floatingCtaTarget.value;
		return target ? (sections.get(target)?.isVisible.value ?? false) : false;
	});

	const hasScrolledPastTarget = computed<boolean>(() => {
		const target = floatingCtaTarget.value;
		return target
			? (sections.get(target)?.hasScrolledPast.value ?? false)
			: false;
	});

	const shouldShowFloatingCta = computed<boolean>(
		() =>
			floatingCtaTarget.value !== null &&
			!isHeroVisible.value &&
			!isTargetSectionVisible.value &&
			!hasScrolledPastTarget.value
	);

	function getSectionVisibility(key: FloatingCtaTarget): boolean {
		return sections.get(key)?.isVisible.value ?? false;
	}

	return {
		registerSection,

		isHeroVisible: readonly(isHeroVisible),

		floatingCtaTarget,
		targetRef,
		shouldShowFloatingCta,

		/** @internal For consumers that need per-section visibility (e.g. debugging) */
		getSectionVisibility
	};
}
